import { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { motion, AnimatePresence, useInView, useScroll, useTransform } from "framer-motion"
import {
  ShoppingBag, X, Plus, Minus, ArrowRight,
  Check, AlertCircle, Loader2, ChevronDown
} from "lucide-react"

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────────────────────────────────────
const t = {
  cream:    "#1A1A1B",
  sand:     "#2a2a2c",
  warm:     "#0D0D0D",
  accent:   "#07a805",
  accentDk: "#0D0D0D",
  text:     "#FFFFFF",
  muted:    "#888888",
  border:   "rgba(255,255,255,0.08)",
  glassBg:  "rgba(20, 20, 22, 0.4)",
  glassBorder: "rgba(255,255,255,0.1)",
  neonGlow: "0 0 15px rgba(7,168,5,0.3)",
}

// ─────────────────────────────────────────────────────────────────────────────
// PARTÍCULA DE CONFIRMACIÓN (FLY TO CART)
// ─────────────────────────────────────────────────────────────────────────────
function ConfirmationParticle({ startPos, targetPos, onComplete }) {
  return (
    <motion.div
      initial={{ x: startPos.x, y: startPos.y, opacity: 1, scale: 1 }}
      animate={{ x: targetPos.x, y: targetPos.y, opacity: 0, scale: 0.2 }}
      transition={{ duration: 0.6, ease: "easeIn" }}
      onAnimationComplete={onComplete}
      className="fixed z-[1000] pointer-events-none w-3 h-3 rounded-full"
      style={{ backgroundColor: t.accent, boxShadow: t.neonGlow }}
    />
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTES DE MEDUSA
// ─────────────────────────────────────────────────────────────────────────────
const MEDUSA_API_URL = import.meta.env.VITE_MEDUSA_API_URL || "";
const PUBLISHABLE_API_KEY = import.meta.env.VITE_PUBLISHABLE_API_KEY;
const REGION_ID = "reg_01KVTZ4D3ZQ123K80J28T4YZE2"  // Latinoamérica (CRC)
const isDev = import.meta.env.MODE === 'development'

// ─────────────────────────────────────────────────────────────────────────────
// HELPER: SÍMBOLO DE MONEDA
// ─────────────────────────────────────────────────────────────────────────────
function getCurrencySymbol(code) {
  const symbols = {
    eur: '€',
    usd: '$',
    crc: '₡',
    gbp: '£',
    jpy: '¥',
    cny: '¥',
  }
  return symbols[code?.toLowerCase()] || code?.toUpperCase() || ''
}

// ─────────────────────────────────────────────────────────────────────────────
// FUNCIONES DE API (MEDUSA v2)
// ─────────────────────────────────────────────────────────────────────────────
async function fetchMedusaProducts(limit = 50) {
  const baseUrl = isDev ? '' : MEDUSA_API_URL

  // ✅ Incluimos TODOS los campos necesarios
  const fields = [
    'id',
    'title',
    'description',
    'subtitle',
    'thumbnail',
    '*images',
    'options',
    'type.value',
    'categories.name',
    'collection.title',
    'tags',
    'variants.calculated_price',
    'variants.inventory_quantity',
    'variants.manage_inventory',
    'variants.sku',
    'variants.allow_backorder'
  ].join(',')

  const url = `${baseUrl}/store/products?limit=${limit}&region_id=${REGION_ID}&fields=${fields}`

  console.log('📡 Petición a Medusa:', url)

  const response = await fetch(url, {
    headers: {
      "x-publishable-api-key": PUBLISHABLE_API_KEY,
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    throw new Error(`Error fetching products: ${response.status} ${response.statusText}`)
  }

  const data = await response.json()
  console.log('📦 Productos recibidos:', data.products?.length || 0)
  return data.products || []
}

// ─────────────────────────────────────────────────────────────────────────────
// NORMALIZACIÓN DE PRODUCTOS (MEDUSA → FRONTEND)
// ─────────────────────────────────────────────────────────────────────────────
function normalizeMedusaProduct(medusaProduct) {
  // Extraer todas las variantes con sus precios, stock, etc.
  const variants = medusaProduct.variants?.map(v => {
    const priceObj = v.calculated_price;
    return {
      id: v.id,
      sku: v.sku || medusaProduct.handle || '',
      price: priceObj?.calculated_amount ?? 0,
      currency_code: priceObj?.currency_code ?? 'crc',
      inventory_quantity: v.inventory_quantity ?? 0,
      manage_inventory: v.manage_inventory ?? false,
      allow_backorder: v.allow_backorder ?? false,
      // Título de la variante (ej: "S / Black")
      title: v.title || '',
      // Opciones asociadas (para mostrar talla, color)
      options: v.options?.map(opt => ({
        option_id: opt.option_id,
        option_title: opt.option?.title || '',
        value: opt.value,
      })) || [],
    };
  }) || [];

  // Tomar la primera variante como base para campos comunes (precio por defecto)
  const defaultVariant = variants[0] || {};

  // Precio y moneda por defecto
  const price = defaultVariant.price ?? 0;
  const currencyCode = defaultVariant.currency_code ?? 'crc';

  // Stock base (solo se usa si no hay variantes)
  const manageInventory = defaultVariant.manage_inventory ?? false;
  const inventoryQty = defaultVariant.inventory_quantity ?? 0;
  const allowBackorder = defaultVariant.allow_backorder ?? false;
  const isOutOfStock = manageInventory && inventoryQty <= 0 && !allowBackorder;

  // Extraer colores y tallas desde options (para mostrar en la UI)
  const colors = [];
  const sizes = [];
  medusaProduct.options?.forEach(option => {
    if (option.title?.toLowerCase() === 'color') {
      option.values?.forEach(val => { if (val.value) colors.push(val.value); });
    }
    if (option.title?.toLowerCase() === 'size') {
      option.values?.forEach(val => { if (val.value) sizes.push(val.value); });
    }
  });

  // Categoría (usando categories, type, collection o tags)
  let category = 'General';
  if (medusaProduct.categories && medusaProduct.categories.length > 0) {
    category = medusaProduct.categories[0].name;
  } else if (medusaProduct.type?.value) {
    category = medusaProduct.type.value;
  } else if (medusaProduct.collection?.title) {
    category = medusaProduct.collection.title;
  } else if (medusaProduct.tags && medusaProduct.tags.length > 0) {
    category = medusaProduct.tags[0].value || 'General';
  }

  // Imágenes
  const images = medusaProduct.images?.map(img => ({ image_url: img.url })) || [];

  return {
    id: medusaProduct.id,
    name: medusaProduct.title || 'Producto sin nombre',
    desc: medusaProduct.description || '',
    label: medusaProduct.subtitle || '',
    price: price, // precio por defecto (primera variante)
    currency_code: currencyCode,
    category: category,
    img: medusaProduct.thumbnail || '',
    images: images,
    colors: colors,
    sizes: sizes,
    sku: defaultVariant.sku || medusaProduct.handle || '',
    inventory_quantity: inventoryQty,
    manage_inventory: manageInventory,
    allow_backorder: allowBackorder,
    is_out_of_stock: isOutOfStock,
    // NUEVO: Guardamos todas las variantes
    variants: variants,
    // Guardamos también la variante por defecto (primera)
    default_variant_id: defaultVariant.id || null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// FUNCIÓN PARA SINPE MÓVIL (Custom Workflow en Medusa)
// ─────────────────────────────────────────────────────────────────────────────
async function createSinpeOrder(cartItems, clientData) {
  // ✅ Usa price del ítem (precio de la variante elegida), no product.price
  const total = cartItems.reduce((s, { price, qty }) => s + price * qty, 0)

  const items = cartItems.map(({ product, variant_id, qty, sku }) => ({
    product_id: product.id,
    variant_id: variant_id,
    name: product.name,
    quantity: qty,
    sku: sku || product.sku || "",
    // precio omitido: el backend recalcula desde Medusa para prevenir fraude
  }))

  const payload = {
    client_name: clientData.client_name,
    client_phone: clientData.client_phone,
    sinpe_voucher: clientData.sinpe_voucher,
    currency_code: "crc",
    items,
    region_id: REGION_ID,
  }

  const baseUrl = isDev ? '' : MEDUSA_API_URL
  const res = await fetch(`${baseUrl}/store/custom/sinpe-order`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-publishable-api-key": PUBLISHABLE_API_KEY,
    },
    body: JSON.stringify(payload),
  })

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}))
    throw new Error(errData.message || `Error ${res.status}: ${res.statusText}`)
  }
  return res.json()
}

// ─────────────────────────────────────────────────────────────────────────────
// FUNCIÓN PARA PAYPAL
// ─────────────────────────────────────────────────────────────────────────────
async function createPayPalOrder(cartItems) {
  const total = cartItems.reduce((s, { product, qty }) => s + product.price * qty, 0)
  const payload = {
    currency: "CRC",
    total: total.toFixed(2),
    items: cartItems.map(({ product, qty }) => ({
      product_id: product.id,
      variant_id: product.variant_id || null,
      name: product.name,
      unit_amount: { currency_code: "CRC", value: product.price.toFixed(2) },
      quantity: String(qty),
      category: "PHYSICAL_GOODS",
    })),
    region_id: REGION_ID,
  }

  const baseUrl = isDev ? '' : MEDUSA_API_URL
  const res = await fetch(`${baseUrl}/store/custom/paypal-order`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-publishable-api-key": PUBLISHABLE_API_KEY,
    },
    body: JSON.stringify(payload),
  })

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}))
    throw new Error(errData.message || `Error ${res.status}: ${res.statusText}`)
  }
  return res.json()
}

// ─────────────────────────────────────────────────────────────────────────────
// WHATSAPP HELPER
// ─────────────────────────────────────────────────────────────────────────────
const phoneNumber = "50685077582"

function generateWhatsAppLink(message) {
  const cleanNumber = phoneNumber.replace(/\D/g, '')
  const encodedMsg = encodeURIComponent(message)
  return `https://wa.me/${cleanNumber}?text=${encodedMsg}`
}

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION VARIANTS
// ─────────────────────────────────────────────────────────────────────────────
const easeOut = [0.22, 1, 0.36, 1]
const easeIn  = [0.32, 0, 0.67, 0]

const fadeUp = {
  hidden:  { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.65, ease: easeOut, delay: i * 0.09 },
  }),
}

const drawerVariants = {
  closed: { x: "100%", transition: { duration: 0.38, ease: easeIn } },
  open:   { x: 0,      transition: { duration: 0.52, ease: easeOut } },
}

const overlayVariants = {
  closed: { opacity: 0 },
  open:   { opacity: 1, transition: { duration: 0.3 } },
}

const modalVariants = {
  hidden:  { opacity: 0, scale: 0.96, y: 24 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.45, ease: easeOut } },
  exit:    { opacity: 0, scale: 0.95, y: 8, transition: { duration: 0.22 } },
}

// ─────────────────────────────────────────────────────────────────────────────
// NAVBAR
// ─────────────────────────────────────────────────────────────────────────────
function Navbar({ cartCount, onCartOpen, cartIconRef }) {
    const [scrolled, setScrolled] = useState(false)
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 50)
        window.addEventListener("scroll", onScroll, { passive: true })
        return () => window.removeEventListener("scroll", onScroll)
    }, [])

    return (
        <motion.nav
        initial={{ y: -20, opacity: 0.3 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: easeOut }}
        style={{
            position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
            backgroundColor: scrolled ? t.glassBg : "transparent",
            backdropFilter: scrolled ? "blur(20px)" : "none",
            borderBottom: scrolled ? `1px solid ${t.glassBorder}` : "1px solid transparent",
            transition: "all 0.4s ease",
            padding: "0 clamp(1.5rem, 5vw, 3.5rem)",
        }}
        >
        <div style={{
            maxWidth: 1400, margin: "0 auto",
            display: "flex", alignItems: "center",
            justifyContent: "space-between", height: 72,
            position: "relative",
        }}>
        <nav style={{ display: "flex", gap: "2.5rem" }}>
        <a href="https://www.dac-cloud.com" target="_blank" rel="noopener noreferrer" style={{
            fontFamily: "'Jost', sans-serif", fontSize: "0.7rem",
            letterSpacing: "0.15em", textTransform: "uppercase",
            color: t.text, textDecoration: "none", opacity: 0.55,
            transition: "opacity 0.25s",
        }} onMouseEnter={e => (e.target.style.opacity = 1)}
        onMouseLeave={e => (e.target.style.opacity = 0.55)}>
        COMPRAR
        </a>
        </nav>

        <a href="/" style={{
            textDecoration: "none", position: "absolute", left: "50%",
            transform: "translateX(-50%)",
        }}>
        <span style={{
            fontFamily: "'Cormorant Garamond', serif", fontSize: "1.2rem",
            letterSpacing: "0.3em", fontWeight: 300, color: t.text,
            textTransform: "uppercase", whiteSpace: "nowrap",
            backgroundColor: t.glassBg,
            backdropFilter: "blur(10px)",
            padding: "0.2em 0.5em",
            border: `1px solid ${t.glassBorder}`,
            display: "inline-block"
        }}>Balanz</span>
        </a>

        <button
        ref={cartIconRef}
        onClick={onCartOpen}
        aria-label="Abrir bolsa"
        style={{
            background: "none", border: "none", cursor: "pointer",
            position: "relative", padding: "6px 8px", color: t.text,
            display: "flex", alignItems: "center", gap: "0.55rem",
        }}
        >
        <ShoppingBag size={19} strokeWidth={1.5} />
        <AnimatePresence>
        {cartCount > 0 && (
            <motion.span
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 20 }}
            style={{
                position: "absolute", top: 0, right: 0,
                width: 17, height: 17, borderRadius: "50%",
                backgroundColor: t.accent, color: "#000",
                fontSize: "0.58rem", fontFamily: "'Jost', sans-serif",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 500, boxShadow: t.neonGlow,
            }}
            >
            {cartCount > 9 ? "9+" : cartCount}
            </motion.span>
        )}
        </AnimatePresence>
        <span style={{
            fontFamily: "'Jost', sans-serif", fontSize: "0.68rem",
            letterSpacing: "0.12em", textTransform: "uppercase", opacity: 0.55,
        }}>Bolsa</span>
        </button>
        </div>
        </motion.nav>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// HERO
// ─────────────────────────────────────────────────────────────────────────────
function Hero() {
    const heroRef = useRef(null)
    const { scrollY } = useScroll({ target: heroRef })
    const scale = useTransform(scrollY, [0, 500], [1, 1.08])
    const y = useTransform(scrollY, [0, 500], [0, -30])

    return (
        <section ref={heroRef} style={{
            minHeight: "80vh", position: "relative",
            display: "flex", alignItems: "flex-end",
            backgroundColor: t.cream, overflow: "hidden",
        }}>
        <motion.video
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 20%", scale, y }}
        autoPlay muted loop playsInline preload="auto"
        poster="https://assets.dac-web.dac-cloud.com/maison-nua-assets/internos/promo_balanz_04.avif"
        >
        <source src="https://assets.dac-web.dac-cloud.com/maison-nua-assets/internos/balanz_promo_01.mp4" type="video/mp4" />
        </motion.video>

        <div style={{
            position: "absolute", inset: 0,
            background: `linear-gradient(to top, rgba(1A1A1B,0.95) 0%, rgba(1A1A1B,0.4) 40%, rgba(1A1A1B,0.05) 70%)`,
        }} />

        <div style={{
            position: "relative", zIndex: 1, width: "100%", maxWidth: 1400,
            margin: "0 auto", padding: "clamp(2rem, 5vw, 4rem) clamp(1.5rem, 5vw, 3.5rem)",
            paddingBottom: "clamp(5rem, 10vw, 9rem)",
        }}>
        <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: easeOut }}
        style={{
            background: t.glassBg,
            backdropFilter: "blur(20px)",
            border: `1px solid ${t.glassBorder}`,
            borderRadius: 24,
            padding: "clamp(2rem, 4vw, 3rem)",
            maxWidth: "max-content",
            boxShadow: "0 20px 40px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)",
        }}
        >
        <motion.p
        style={{
            fontFamily: "'Jost', sans-serif", fontSize: "0.68rem",
            letterSpacing: "0.22em", textTransform: "uppercase",
            color: t.accent, marginBottom: "1.25rem",
        }}
        >Summer Drop — 2026</motion.p>
        <motion.h1
        style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "clamp(3.8rem, 10vw, 9rem)",
            fontWeight: 300, lineHeight: 0.88, color: t.text,
            letterSpacing: "-0.02em", marginBottom: "2.5rem", maxWidth: "13ch",
        }}
        >
        Encuentra<br /><em style={{ fontStyle: "italic", fontWeight: 300 }}>tú</em><br />centro
        </motion.h1>
        <motion.div
        style={{ display: "flex", gap: "1.5rem", alignItems: "center", flexWrap: "wrap" }}
        >
        <a href="#catalog" className="hero-cta" style={{
            display: "inline-flex", alignItems: "center", gap: "0.75rem",
            fontFamily: "'Jost', sans-serif", fontSize: "0.75rem",
            letterSpacing: "0.16em", textTransform: "uppercase",
            color: t.text, textDecoration: "none",
            padding: "0.8rem 1.5rem",
            background: "rgba(255,255,255,0.05)",
            backdropFilter: "blur(10px)",
            border: `1px solid ${t.glassBorder}`,
            borderRadius: 40,
            transition: "all 0.35s ease",
        }} onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)" }}
        onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.borderColor = t.glassBorder }}>
        Explorar colección <ArrowRight size={13} strokeWidth={1.5} />
        </a>
        </motion.div>
        </motion.div>
        </div>
        <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ delay: 1.4 }}
        style={{
            position: "absolute", bottom: "2rem", right: "clamp(1.5rem, 5vw, 3.5rem)",
            zIndex: 2, display: "flex", flexDirection: "column", alignItems: "center",
        }}
        >
        <motion.div animate={{ y: [0, 7, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
        <ChevronDown size={16} strokeWidth={1.5} color={t.muted} />
        </motion.div>
        </motion.div>
        </section>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// VIDEO REELS SECTION
// ─────────────────────────────────────────────────────────────────────────────
const ReelItem = ({ video, poster, name, priority }) => {
  const containerRef = useRef(null)
  const videoRef = useRef(null)
  const loadTrigger = useInView(containerRef, { amount: 0, margin: "800px 0px" })
  const playTrigger = useInView(containerRef, { amount: 0.5 })
  const [shouldLoad, setShouldLoad] = useState(priority)

  useEffect(() => {
    if (loadTrigger || priority) setShouldLoad(true)
  }, [loadTrigger, priority])

  useEffect(() => {
    if (!videoRef.current || !shouldLoad) return
    playTrigger ? videoRef.current.play().catch(e => {}) : videoRef.current.pause()
  }, [playTrigger, shouldLoad])

  return (
    <div
      ref={containerRef}
      style={{
        flex: "0 0 280px",
        scrollSnapAlign: "start",
        borderRadius: 20,
        overflow: "hidden",
        position: "relative",
        background: t.glassBg,
        backdropFilter: "blur(10px)",
        border: `1px solid ${t.glassBorder}`,
      }}
    >
      {shouldLoad ? (
        <video
          ref={videoRef}
          src={video}
          poster={poster}
          controls
          muted
          loop
          playsInline
          preload={priority ? "auto" : "metadata"}
          style={{
            width: "100%",
            aspectRatio: "9/16",
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : (
        <img
          src={poster}
          alt={name}
          loading="lazy"
          style={{
            width: "100%",
            aspectRatio: "9/16",
            objectFit: "cover",
            display: "block",
          }}
        />
      )}
      <div style={{
        position: "absolute",
        bottom: 0, left: 0, right: 0,
        padding: "1rem",
        background: "linear-gradient(to top, rgba(0,0,0,0.6), transparent)",
        color: "white",
        fontFamily: "'Cormorant Garamond', serif",
        fontSize: "0.9rem",
        fontWeight: 400,
        pointerEvents: "none",
      }}>
        {name}
      </div>
    </div>
  )
}

function VideoReels() {
  const [reels, setReels] = useState([])

  useEffect(() => {
    fetch("/store/custom/reels")
      .then((r) => r.json())
      .then((data) => setReels(data.reels || []))
      .catch(() => setReels([]))
  }, [])

  if (reels.length === 0) return null

  return (
    <section style={{
      padding: "clamp(4rem, 8vw, 6rem) clamp(1.5rem, 5vw, 3.5rem)",
      backgroundColor: t.cream,
    }}>
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ marginBottom: "2.5rem" }}
        >
          <p style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.68rem", letterSpacing: "0.22em", textTransform: "uppercase", color: t.accent, marginBottom: "0.5rem" }}>Reels</p>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 4vw, 2.5rem)", fontWeight: 300, color: t.text }}>Vive la experiencia Balanz</h2>
        </motion.div>

        <div style={{ display: "flex", overflowX: "auto", scrollSnapType: "x mandatory", gap: "1.5rem", paddingBottom: "1rem", WebkitOverflowScrolling: "touch" }}>
          {reels.map((p, idx) => (
            <ReelItem key={p.id} video={p.video_url} poster={p.poster_url} name={p.name} priority={idx < 2} />
          ))}
        </div>
        <p style={{ textAlign: "center", marginTop: "1.8rem", fontFamily: "'Jost', sans-serif", fontSize: "0.7rem", color: t.muted }}>
          Desliza lateralmente para ver más reels
        </p>
      </div>
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// PRODUCT CARD
// ─────────────────────────────────────────────────────────────────────────────
function ProductCard({ product, onAddToCart, index, onFlyStart }) {
  const cardRef = useRef(null);
  const inView = useInView(cardRef, { once: true, margin: '-60px' });
  const [hovered, setHovered] = useState(false);
  const [added, setAdded] = useState(false);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [shadowPos, setShadowPos] = useState({ x: 0, y: 0 });

  // Estado para el modal de variantes
  const [showVariantModal, setShowVariantModal] = useState(false);

  // Todas las imágenes
  const allImages = [product.img, ...(product.images?.map(img => img.image_url) || [])].filter(Boolean);
  const hasMultiple = allImages.length > 1;

  // Precarga
  useEffect(() => {
    allImages.slice(1).forEach(url => {
      const img = new Image();
      img.src = url;
    });
  }, [allImages]);

  useEffect(() => {
    let timer;
    if (hovered && hasMultiple) {
      timer = setTimeout(() => setCurrentImgIndex(1), 80);
    } else {
      timer = setTimeout(() => setCurrentImgIndex(0), 80);
    }
    return () => clearTimeout(timer);
  }, [hovered, hasMultiple]);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -8;
    const rotateY = ((x - centerX) / centerX) * 8;
    setTilt({ x: rotateX, y: rotateY });
    setShadowPos({ x: (x - centerX) * 0.1, y: (y - centerY) * 0.1 });
  };

  const handleMouseEnter = () => setHovered(true);
  const handleMouseLeave = () => {
    setHovered(false);
    setTilt({ x: 0, y: 0 });
    setShadowPos({ x: 0, y: 0 });
  };

  const nextImage = (e) => { e.stopPropagation(); setCurrentImgIndex((prev) => (prev + 1) % allImages.length); };
  const prevImage = (e) => { e.stopPropagation(); setCurrentImgIndex((prev) => (prev - 1 + allImages.length) % allImages.length); };

  // Manejador del clic en "Añadir a bolsa"
  const handleAddClick = (e) => {
    e.stopPropagation();

    // Si el producto tiene más de una variante, abrir modal
    if (product.variants && product.variants.length > 1) {
      setShowVariantModal(true);
      return;
    }

    // Si solo tiene una variante, añadir directamente
    const variant = product.variants?.[0];
    if (!variant) return;

    // Llamar a onAddToCart con el producto y la variante
    if (!added && !product.is_out_of_stock) {
      const rect = e.currentTarget.getBoundingClientRect();
      onFlyStart({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
      onAddToCart(product, variant); // Pasamos la variante
      setAdded(true);
      setTimeout(() => setAdded(false), 1800);
    }
  };

  // Función para añadir desde el modal (con variante seleccionada)
  const handleAddWithVariant = (product, selectedVariant) => {
    // Calcular posición para la partícula (desde el botón del modal)
    // Podríamos pasarlo desde el modal, pero simplificamos: usamos el cartIconRef
    // (se dispara la partícula desde el modal)
    if (cartIconRef && cartIconRef.current) {
      const rect = cartIconRef.current.getBoundingClientRect();
      const target = { x: rect.left + rect.width/2, y: rect.top + rect.height/2 };
      // Llamamos al fly desde el modal (lo haremos con un callback)
      // Necesitamos pasar onFlyStart desde el padre.
      // Mejor: usamos un prop para el fly, lo pasamos desde el modal.
      // Como es complejo, delegamos la partícula al modal llamando a onFlyStart si se pasa.
    }
    onAddToCart(product, selectedVariant);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  const currencySymbol = getCurrencySymbol(product.currency_code);
  const formattedPrice = product.price.toFixed(2);
  const outOfStock = product.is_out_of_stock;

  return (
    <>
      <motion.article
        ref={cardRef}
        variants={fadeUp}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        custom={index % 3}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          cursor: "pointer",
          background: t.glassBg,
          backdropFilter: "blur(10px)",
          border: `1px solid ${t.glassBorder}`,
          borderRadius: 20,
          overflow: "hidden",
          transform: `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          boxShadow: `${shadowPos.x}px ${shadowPos.y}px 25px rgba(0,0,0,0.4), 0 0 20px rgba(7,168,5,0.1)`,
          transition: "transform 0.1s ease-out, box-shadow 0.1s ease-out",
        }}
      >
        <div style={{ position: "relative", overflow: "hidden", backgroundColor: t.sand, aspectRatio: "3/4" }}>
          <motion.img
            key={currentImgIndex}
            src={allImages[currentImgIndex] || "https://via.placeholder.com/300x400?text=Sin+imagen"}
            animate={{ scale: hovered ? 1.05 : 1 }}
            transition={{ duration: 0.75 }}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            alt={product.name}
            loading="lazy"
          />

          {hovered && hasMultiple && (
            <>
              <button onClick={prevImage} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.4)", border: "none", borderRadius: 20, width: 32, height: 32, color: "white", cursor: "pointer", zIndex: 2, backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center" }}>◀</button>
              <button onClick={nextImage} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.4)", border: "none", borderRadius: 20, width: 32, height: 32, color: "white", cursor: "pointer", zIndex: 2, backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center" }}>▶</button>
              <div style={{ position: "absolute", bottom: 8, left: 0, right: 0, textAlign: "center", zIndex: 2 }}>
                {allImages.map((_, idx) => (
                  <span key={idx} style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: idx === currentImgIndex ? "white" : "rgba(255,255,255,0.5)", margin: "0 3px" }} />
                ))}
              </div>
            </>
          )}

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: hovered ? 1 : 0, y: hovered ? 0 : 8 }}
            transition={{ duration: 0.28, ease: easeOut }}
            style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "1.1rem", zIndex: 1 }}
          >
            <motion.button
              whileTap={{ scale: 0.96 }}
              onClick={handleAddClick}
              disabled={outOfStock}
              style={{
                width: "100%",
                padding: "0.8rem 1rem",
                backgroundColor: outOfStock ? "rgba(100,100,100,0.5)" : (added ? t.accent : t.glassBg),
                color: outOfStock ? t.muted : t.text,
                border: `1px solid ${outOfStock ? t.border : t.glassBorder}`,
                cursor: outOfStock ? "not-allowed" : "pointer",
                fontFamily: "'Jost', sans-serif",
                fontSize: "0.68rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
                backdropFilter: "blur(15px)",
                borderRadius: 12,
                boxShadow: added ? t.neonGlow : "none",
                transition: "all 0.35s ease",
              }}
            >
              <AnimatePresence mode="wait">
                {outOfStock ? (
                  <motion.span key="outofstock" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    Sin stock
                  </motion.span>
                ) : added ? (
                  <motion.span key="added" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                    <Check size={12} strokeWidth={2} /> Añadido
                  </motion.span>
                ) : (
                  <motion.span key="add" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                    Añadir a bolsa
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
          </motion.div>
        </div>

        <div style={{ padding: "1.2rem" }}>
          <p style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.6rem", letterSpacing: "0.16em", textTransform: "uppercase", color: t.accent, marginBottom: "0.3rem" }}>
            {product.category}
          </p>
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.15rem", fontWeight: 400, color: t.text, marginBottom: "0.3rem", letterSpacing: "0.01em" }}>
            {product.name}
          </h3>
          <p style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.75rem", color: t.muted, lineHeight: 1.6, marginBottom: "0.55rem" }}>
            {product.desc}
          </p>

          <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.1rem", fontWeight: 400, color: t.text }}>
            {currencySymbol}{formattedPrice}
            {product.currency_code && product.currency_code !== 'crc' && (
              <span style={{ fontSize: "0.7rem", color: t.muted, marginLeft: "0.3rem" }}>
                ({product.currency_code.toUpperCase()})
              </span>
            )}
          </p>

          {product.colors && product.colors.length > 0 && (
            <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
              {product.colors.map((color, idx) => (
                <div key={idx} style={{ width: 20, height: 20, borderRadius: "50%", backgroundColor: color.toLowerCase(), border: "1px solid rgba(255,255,255,0.2)" }} title={color} />
              ))}
            </div>
          )}

          {product.sizes && product.sizes.length > 0 && (
            <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
              {product.sizes.map((size, idx) => (
                <span key={idx} style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.7rem", color: t.muted }}>{size}</span>
              ))}
            </div>
          )}

          {outOfStock && (
            <span style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.6rem", color: "#ff4444", marginTop: "0.3rem", display: "block" }}>
              ⚠️ Limited stock
            </span>
          )}
        </div>
      </motion.article>

      {/* Modal de selección de variantes */}
      <VariantSelectorModal
        isOpen={showVariantModal}
        onClose={() => setShowVariantModal(false)}
        product={product}
        onAddToCartWithVariant={onAddToCart}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODAL DE SELECCIÓN DE VARIANTES
// ─────────────────────────────────────────────────────────────────────────────
function VariantSelectorModal({ isOpen, onClose, product, onAddToCartWithVariant }) {
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);

  // Reiniciar selección al abrir
  useEffect(() => {
    if (isOpen && product) {
      // Seleccionar la primera variante por defecto
      const first = product.variants?.[0];
      if (first) {
        setSelectedVariantId(first.id);
        setSelectedVariant(first);
      }
    }
  }, [isOpen, product]);

  if (!isOpen || !product) return null;

  const handleSelectVariant = (variantId) => {
    const variant = product.variants.find(v => v.id === variantId);
    if (variant) {
      setSelectedVariantId(variantId);
      setSelectedVariant(variant);
    }
  };

  const handleAddToCart = () => {
    if (!selectedVariant) return;
    // Llamar a la función padre con los datos de la variante seleccionada
    onAddToCartWithVariant(product, selectedVariant);
    onClose();
  };

  const currencySymbol = getCurrencySymbol(product.currency_code);
  const priceToShow = selectedVariant?.price ?? product.price;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 500,
        backgroundColor: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <motion.div
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: t.glassBg,
          backdropFilter: 'blur(30px)',
          border: `1px solid ${t.glassBorder}`,
          borderRadius: 24,
          width: 'min(500px, 100%)',
          padding: 'clamp(1.75rem, 4vw, 2.75rem)',
          position: 'relative',
          boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1.25rem',
            right: '1.25rem',
            background: 'none',
            border: 'none',
            color: t.muted,
          }}
        >
          <X size={18} />
        </button>

        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.6rem', fontWeight: 300, color: t.text, marginBottom: '0.5rem' }}>
          {product.name}
        </h2>

        {/* Precio actualizado */}
        <p style={{ fontFamily: "'Jost', sans-serif", fontSize: '1.2rem', color: t.text, marginBottom: '1.5rem' }}>
          {currencySymbol}{priceToShow.toFixed(2)}
          {product.currency_code && product.currency_code !== 'crc' && (
            <span style={{ fontSize: '0.7rem', color: t.muted, marginLeft: '0.3rem' }}>
              ({product.currency_code.toUpperCase()})
            </span>
          )}
        </p>

        {/* Opciones de variantes agrupadas por tipo (talla, color, etc.) */}
        {product.variants && product.variants.length > 0 && (
          <div>
            {/* Agrupar opciones por título de opción (ej: "Size", "Color") */}
            {Object.entries(
              product.variants.reduce((acc, v) => {
                v.options?.forEach(opt => {
                  const key = opt.option_title;
                  if (!acc[key]) acc[key] = new Set();
                  acc[key].add(opt.value);
                });
                return acc;
              }, {})
            ).map(([optionTitle, valuesSet]) => {
              const values = Array.from(valuesSet);
              return (
                <div key={optionTitle} style={{ marginBottom: '1.25rem' }}>
                  <p style={{ fontFamily: "'Jost', sans-serif", fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: t.muted, marginBottom: '0.5rem' }}>
                    {optionTitle}
                  </p>
                  <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                    {values.map(val => {
                      // Buscar la primera variante que tenga esta combinación (para saber el stock)
                      const variantForVal = product.variants.find(v =>
                        v.options?.some(o => o.option_title === optionTitle && o.value === val)
                      );
                      const isSelected = selectedVariant?.options?.some(o => o.option_title === optionTitle && o.value === val);
                      const inStock = variantForVal?.inventory_quantity > 0 || variantForVal?.allow_backorder;
                      return (
                        <button
                          key={val}
                          onClick={() => {
                            // Encontrar la variante que coincida con todas las opciones seleccionadas
                            // Para simplificar, seleccionamos la primera que tenga esta opción.
                            // En una implementación más robusta, se debe manejar combinaciones.
                            const found = product.variants.find(v =>
                              v.options?.some(o => o.option_title === optionTitle && o.value === val)
                            );
                            if (found) handleSelectVariant(found.id);
                          }}
                          disabled={!inStock}
                          style={{
                            padding: '0.4rem 1rem',
                            borderRadius: 30,
                            border: `1px solid ${isSelected ? t.accent : t.glassBorder}`,
                            backgroundColor: isSelected ? t.accent : 'transparent',
                            color: isSelected ? '#000' : (inStock ? t.text : t.muted),
                            cursor: inStock ? 'pointer' : 'not-allowed',
                            fontFamily: "'Jost', sans-serif",
                            fontSize: '0.75rem',
                            transition: 'all 0.2s',
                          }}
                        >
                          {val}
                          {!inStock && ' (Sin stock)'}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <button
          onClick={handleAddToCart}
          disabled={!selectedVariant}
          style={{
            width: '100%',
            padding: '0.9rem',
            backgroundColor: t.accent,
            color: '#000',
            border: 'none',
            borderRadius: 40,
            fontFamily: "'Jost', sans-serif",
            fontSize: '0.75rem',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            cursor: selectedVariant ? 'pointer' : 'not-allowed',
            marginTop: '1rem',
            boxShadow: t.neonGlow,
          }}
        >
          Añadir a bolsa
        </button>
      </motion.div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CATALOG SECTION
// ─────────────────────────────────────────────────────────────────────────────
function CatalogSection({ products, onAddToCart, loading, error, onFlyStart }) {
    const sectionRef = useRef(null)
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 })
    const [activeCategory, setActiveCategory] = useState("Todos")
    const categories = useMemo(() => {
        const cats = products.map(p => p.category).filter(Boolean)
        return ["Todos", ...new Set(cats)]
    }, [products])

    const sortedProducts = useMemo(() => [...products].sort((a, b) => (a.sku || '').localeCompare(b.sku || '')), [products])
    const filtered = activeCategory === "Todos" ? sortedProducts : sortedProducts.filter(p => p.category === activeCategory)

    const handleMouseMove = (e) => {
        if (!sectionRef.current) return
            const rect = sectionRef.current.getBoundingClientRect()
            setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
    }

    if (loading) return (
        <section id="catalog" style={{ padding: "clamp(5rem, 9vw, 10rem) clamp(1.5rem, 5vw, 3.5rem)", maxWidth: 1400, margin: "0 auto", textAlign: "center" }}>
        <Loader2 size={40} strokeWidth={1.5} style={{ animation: "spin 1s linear infinite", color: t.accent }} />
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </section>
    )

        if (error) return (
            <section id="catalog" style={{ padding: "clamp(5rem, 9vw, 10rem) clamp(1.5rem, 5vw, 3.5rem)", maxWidth: 1400, margin: "0 auto", textAlign: "center" }}>
            <AlertCircle size={40} strokeWidth={1.5} color={t.accent} />
            <p style={{ color: t.muted }}>{error}</p>
            </section>
        )

            return (
                <section ref={sectionRef} id="catalog" onMouseMove={handleMouseMove} style={{ padding: "clamp(5rem, 9vw, 10rem) clamp(1.5rem, 5vw, 3.5rem)", maxWidth: 1400, margin: "0 auto", position: "relative" }}>
                {/* SPOTLIGHT BACKGROUND */}
                <div style={{
                    position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0,
                    background: `radial-gradient(circle 400px at ${mousePos.x}px ${mousePos.y}px, rgba(7,168,5,0.08), transparent 60%)`,
                    transition: "background 0.2s ease",
                }} />
                <div style={{ position: "relative", zIndex: 1 }}>
                <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: "clamp(2.5rem, 4vw, 4rem)", flexWrap: "wrap", gap: "2rem" }}>
                <div>
                <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.68rem", letterSpacing: "0.22em", textTransform: "uppercase", color: t.accent, marginBottom: "0.7rem" }}>Colección</motion.p>
                <motion.h2 initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.75, ease: easeOut }} style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.1rem, 4.5vw, 3.2rem)", fontWeight: 300, color: t.text, letterSpacing: "-0.015em" }}>Prendas de autor</motion.h2>
                </div>
                <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                {categories.map(cat => (
                    <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    style={{
                        fontFamily: "'Jost', sans-serif", fontSize: "0.66rem", letterSpacing: "0.12em", textTransform: "uppercase",
                        padding: "0.5rem 1.1rem", borderRadius: 40,
                        border: `1px solid ${activeCategory === cat ? t.accent : t.glassBorder}`,
                        backgroundColor: activeCategory === cat ? t.glassBg : "transparent",
                        color: activeCategory === cat ? t.accent : t.muted,
                        cursor: "pointer", transition: "all 0.25s ease", backdropFilter: "blur(5px)",
                    }}
                    onMouseEnter={e => { if (activeCategory !== cat) e.currentTarget.style.borderColor = t.muted }}
                    onMouseLeave={e => { if (activeCategory !== cat) e.currentTarget.style.borderColor = t.glassBorder }}
                    >
                    {cat === "Todos" ? "Todos" : cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </button>
                ))}
                </motion.div>
                </div>
                <motion.div layout style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 270px), 1fr))", gap: "clamp(1.5rem, 3vw, 2.5rem) clamp(1rem, 2.5vw, 2rem)" }}>
                <AnimatePresence mode="popLayout">
                {filtered.map((product, i) => (
                    <motion.div key={product.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.3 }}>
                    <ProductCard product={product} onAddToCart={onAddToCart} index={i} onFlyStart={onFlyStart} />
                    </motion.div>
                ))}
                </AnimatePresence>
                </motion.div>
                </div>
                </section>
            )
}

// ─────────────────────────────────────────────────────────────────────────────
// CART DRAWER
// ─────────────────────────────────────────────────────────────────────────────
function CartDrawer({ isOpen, onClose, cart, onIncrement, onDecrement, onRemove, onOpenCheckout }) {
  const total = cart.reduce((s, { price, qty }) => s + price * qty, 0)
  const itemCount = cart.reduce((s, { qty }) => s + qty, 0)

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : ""
    return () => { document.body.style.overflow = "" }
  }, [isOpen])

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div variants={overlayVariants} initial="closed" animate="open" exit="closed" onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 200, backgroundColor: "rgba(10,10,12,0.6)", backdropFilter: "blur(10px)" }} />
          <motion.aside variants={drawerVariants} initial="closed" animate="open" exit="closed" style={{
            position: "fixed", top: 0, right: 0, bottom: 0, width: "min(430px, 100vw)", zIndex: 201,
            backgroundColor: t.glassBg, backdropFilter: "blur(30px)", borderLeft: `1px solid ${t.glassBorder}`,
            display: "flex", flexDirection: "column", boxShadow: "-10px 0 30px rgba(0,0,0,0.5)",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1.75rem 1.75rem 1.4rem", borderBottom: `1px solid ${t.border}`, flexShrink: 0 }}>
              <div>
                <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.45rem", fontWeight: 400, color: t.text }}>Tu bolsa</h2>
                <p style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.67rem", letterSpacing: "0.1em", color: t.muted, marginTop: "0.2rem" }}>{itemCount} {itemCount === 1 ? "artículo" : "artículos"}</p>
              </div>
              <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: t.muted, padding: 4 }}><X size={20} strokeWidth={1.5} /></button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "1.25rem 1.75rem" }}>
              {cart.length === 0 ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: "1rem", color: t.muted }}>
                  <ShoppingBag size={38} strokeWidth={1} />
                  <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.1rem", fontWeight: 300 }}>Tu bolsa está vacía</p>
                </div>
              ) : (
                <AnimatePresence>
                  {cart.map(({ product, variant_id, variant_title, price, qty }) => (
                    <motion.div key={variant_id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }} style={{ display: "flex", gap: "1rem", paddingBottom: "1.4rem", marginBottom: "1.4rem", borderBottom: `1px solid ${t.border}` }}>
                      <img src={product.img || "https://via.placeholder.com/78x98?text=No+img"} alt={product.name} style={{ width: 78, height: 98, objectFit: "cover", backgroundColor: t.sand, borderRadius: 12, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                          <div>
                            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1rem", fontWeight: 400, color: t.text }}>{product.name}</p>
                            {/* ✅ Mostrar la variante */}
                            {variant_title && (
                              <p style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.65rem", color: t.muted }}>{variant_title}</p>
                            )}
                            <p style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.7rem", color: t.muted, marginTop: "0.1rem" }}>₡{price.toFixed(2)}</p> {/* ✅ Usamos price del ítem */}
                          </div>
                          <button onClick={() => onRemove(variant_id)} style={{ background: "none", border: "none", cursor: "pointer", color: t.muted }}><X size={14} strokeWidth={1.5} /></button> {/* ✅ Pasamos variant_id */}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.65rem", marginTop: "0.75rem" }}>
                          <button onClick={() => onDecrement(variant_id)} style={{ width: 27, height: 27, border: `1px solid ${t.border}`, background: "none", cursor: "pointer", color: t.text, borderRadius: 20 }}><Minus size={11} /></button> {/* ✅ Pasamos variant_id */}
                          <span style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.82rem", minWidth: 18, textAlign: "center", color: t.text }}>{qty}</span>
                          <button onClick={() => onIncrement(variant_id)} style={{ width: 27, height: 27, border: `1px solid ${t.border}`, background: "none", cursor: "pointer", color: t.text, borderRadius: 20 }}><Plus size={11} /></button> {/* ✅ Pasamos variant_id */}
                          <span style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.82rem", color: t.text, marginLeft: "auto" }}>₡{(price * qty).toFixed(2)}</span> {/* ✅ Usamos price del ítem */}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              )}
            </div>
            {cart.length > 0 && (
              <div style={{ padding: "1.5rem 1.75rem", borderTop: `1px solid ${t.border}`, flexShrink: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "1.25rem" }}>
                  <span style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.68rem", letterSpacing: "0.12em", textTransform: "uppercase", color: t.muted }}>Subtotal</span>
                  <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.4rem", fontWeight: 400, color: t.text }}>₡{total.toFixed(2)}</span> {/* ✅ Usa total calculado con price del ítem */}
                </div>
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={onOpenCheckout}
                  style={{
                    width: "100%", padding: "1rem 1.5rem", backgroundColor: t.accent, color: "#000",
                    border: "none", cursor: "pointer", fontFamily: "'Jost', sans-serif",
                    fontSize: "0.72rem", letterSpacing: "0.16em", textTransform: "uppercase",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "0.75rem",
                    borderRadius: 40, boxShadow: t.neonGlow,
                  }}
                >
                  Proceder al pago <ArrowRight size={13} strokeWidth={1.5} />
                </motion.button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// CHECKOUT MODAL
// ─────────────────────────────────────────────────────────────────────────────
function CheckoutModal({ isOpen, onClose, cart, onClearCart }) {
    const [status, setStatus] = useState("idle")
    const [errorMsg, setErrorMsg] = useState("")
    const [formData, setFormData] = useState({ client_name: "", client_phone: "", sinpe_voucher: "" })
    const [createdOrder, setCreatedOrder] = useState(null)
    const total = cart.reduce((s, { price, qty }) => s + price * qty, 0)

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!formData.client_name.trim() || !formData.client_phone.trim() || !formData.sinpe_voucher.trim()) {
            setErrorMsg("Completa todos los campos.")
            return
        }
        setStatus("processing")
        setErrorMsg("")
        try {
            const order = await createSinpeOrder(cart, formData)
            setCreatedOrder(order)
            setStatus("success")
            onClearCart()
        } catch (err) {
            setStatus("error")
            setErrorMsg(err.message || "Error al crear la orden.")
        }
    }

    const handleClose = () => {
        onClose()
        setTimeout(() => {
            setStatus("idle")
            setErrorMsg("")
            setFormData({ client_name: "", client_phone: "", sinpe_voucher: "" })
            setCreatedOrder(null)
        }, 400)
    }

    if (!isOpen) return null

        if (status === "success" && createdOrder) {
            const waLink = generateWhatsAppLink(`*Pago SINPE registrado*\n\nComprobante: ${createdOrder.sinpe_voucher}\nMonto: ₡${createdOrder.total_amount}\nCliente: ${createdOrder.client_name}\nTel: ${createdOrder.client_phone}\n\nAdjunta la captura del comprobante.`)
            return (
                <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={handleClose}
                style={{ position: "fixed", inset: 0, zIndex: 300, backgroundColor: "rgba(10,10,12,0.7)", backdropFilter: "blur(15px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
                >
                <motion.div variants={modalVariants} initial="hidden" animate="visible" exit="exit" onClick={e => e.stopPropagation()} style={{ backgroundColor: t.glassBg, backdropFilter: "blur(30px)", border: `1px solid ${t.glassBorder}`, borderRadius: 24, width: "min(500px, 100%)", padding: "clamp(1.75rem, 4vw, 2.75rem)", position: "relative", boxShadow: "0 20px 40px rgba(0,0,0,0.5)" }}>
                <button onClick={handleClose} style={{ position: "absolute", top: "1.25rem", right: "1.25rem", background: "none", border: "none", color: t.muted }}><X size={18} /></button>
                <Check size={32} color={t.accent} />
                <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.8rem", fontWeight: 300, color: t.text, marginTop: "1rem" }}>¡Orden registrada!</h2>
                <p style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.8rem", color: t.muted, marginBottom: "1.5rem" }}>Envía la captura por WhatsApp para confirmar.</p>
                <div style={{ background: "rgba(0,0,0,0.3)", padding: "1rem", borderRadius: 12, marginBottom: "1.5rem", color: t.text }}>
                <p><strong>Nº comprobante:</strong> {createdOrder.sinpe_voucher}</p>
                <p><strong>Monto:</strong> ₡{Number(createdOrder.total_amount).toFixed(2)}</p>
                </div>
                <a href={waLink} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", width: "100%", padding: "1rem", backgroundColor: "#25D366", color: "white", textDecoration: "none", fontFamily: "'Jost', sans-serif", fontSize: "0.8rem", borderRadius: 40 }}>Enviar comprobante por WhatsApp</a>
                <button onClick={handleClose} style={{ width: "100%", padding: "0.8rem", background: "transparent", border: `1px solid ${t.border}`, borderRadius: 40, color: t.text, marginTop: "0.5rem", cursor: "pointer" }}>Cerrar</button>
                </motion.div>
                </motion.div>
            )
        }

        return (
            <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={status !== "processing" ? handleClose : undefined}
            style={{ position: "fixed", inset: 0, zIndex: 300, backgroundColor: "rgba(10,10,12,0.7)", backdropFilter: "blur(15px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
            >
            <motion.div variants={modalVariants} initial="hidden" animate="visible" exit="exit" onClick={e => e.stopPropagation()} style={{ backgroundColor: t.glassBg, backdropFilter: "blur(30px)", border: `1px solid ${t.glassBorder}`, borderRadius: 24, width: "min(500px, 100%)", padding: "clamp(1.75rem, 4vw, 2.75rem)", position: "relative", boxShadow: "0 20px 40px rgba(0,0,0,0.5)" }}>
            {status !== "processing" && <button onClick={handleClose} style={{ position: "absolute", top: "1.25rem", right: "1.25rem", background: "none", border: "none", color: t.muted }}><X size={18} /></button>}
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.8rem", fontWeight: 300, color: t.text, marginBottom: "0.5rem" }}>Pagar con SINPE Móvil</h2>
            {status === "error" && <div style={{ background: "rgba(255,0,0,0.1)", padding: "0.8rem", borderRadius: 12, marginBottom: "1rem", color: "#ff6666" }}>{errorMsg}</div>}
            <form onSubmit={handleSubmit}>
            <input type="text" placeholder="Nombre completo" value={formData.client_name} onChange={e => setFormData({...formData, client_name: e.target.value})} disabled={status === "processing"} style={{ width: "100%", padding: "0.8rem", marginBottom: "0.8rem", border: `1px solid ${t.border}`, background: "rgba(0,0,0,0.3)", borderRadius: 12, color: t.text, fontFamily: "'Jost', sans-serif" }} />
            <input type="tel" placeholder="Teléfono (ej: 50612345678)" value={formData.client_phone} onChange={e => setFormData({...formData, client_phone: e.target.value})} disabled={status === "processing"} style={{ width: "100%", padding: "0.8rem", marginBottom: "0.8rem", border: `1px solid ${t.border}`, background: "rgba(0,0,0,0.3)", borderRadius: 12, color: t.text }} />
            <input type="text" placeholder="Número de comprobante SINPE" value={formData.sinpe_voucher} onChange={e => setFormData({...formData, sinpe_voucher: e.target.value})} disabled={status === "processing"} style={{ width: "100%", padding: "0.8rem", marginBottom: "1.2rem", border: `1px solid ${t.border}`, background: "rgba(0,0,0,0.3)", borderRadius: 12, color: t.text }} />
            <div style={{ background: "rgba(0,0,0,0.3)", padding: "1rem", borderRadius: 12, marginBottom: "1.5rem" }}>
            {cart.map(({ product, qty }) => (
                <div key={product.id} style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem", color: t.text }}>
                <span>{product.name} × {qty}</span>
                <span>₡{(product.price * qty).toFixed(2)}</span>
                </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", borderTop: `1px solid ${t.border}`, paddingTop: "0.5rem", marginTop: "0.5rem", color: t.text }}><strong>Total</strong><strong>₡{total.toFixed(2)}</strong></div>
            </div>
            <button type="submit" disabled={status === "processing"} style={{ width: "100%", padding: "1rem", backgroundColor: t.accent, color: "#000", border: "none", borderRadius: 40, fontFamily: "'Jost', sans-serif", fontSize: "0.8rem", letterSpacing: "0.16em", cursor: "pointer", boxShadow: t.neonGlow }}>{status === "processing" ? "Registrando..." : `Confirmar pago — ₡${total.toFixed(2)}`}</button>
            </form>
            </motion.div>
            </motion.div>
        )
}

// ─────────────────────────────────────────────────────────────────────────────
// EDITORIAL STRIP
// ─────────────────────────────────────────────────────────────────────────────
function EditorialStrip() {
    const sectionRef = useRef(null)
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 })
    const pillars = [
        { label: "Envíos a todo el país", desc: "Envíos por Correos de Costa Rica y envíos por encomienda Balanz, personal y confiable." },
        { label: "Producción limitada", desc: "Cada pieza, en lotes reducidos. Sin sobreproducción, sin excedentes." },
        { label: "Envío consciente", desc: "Embalaje sin plástico." },
    ]

    const handleMouseMove = (e) => {
        if (!sectionRef.current) return
            const rect = sectionRef.current.getBoundingClientRect()
            setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
    }

    return (
        <section ref={sectionRef} onMouseMove={handleMouseMove} style={{
            backgroundColor: t.text, color: t.cream, position: "relative", overflow: "hidden",
            padding: "clamp(4rem, 7vw, 7rem) clamp(1.5rem, 5vw, 3.5rem)",
        }}>
        <div style={{ position: "absolute", inset: 0, background: `radial-gradient(circle 300px at ${mousePos.x}px ${mousePos.y}px, rgba(7,168,5,0.15), transparent 30%)`, pointerEvents: "none" }} />
        <div style={{ maxWidth: 1400, margin: "0 auto", display: "grid", gap: "2rem", position: "relative", zIndex: 1 }}>
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 0.45 }} viewport={{ once: true }} style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.68rem", letterSpacing: "0.22em", textTransform: "uppercase" }}>Manifiesto</motion.p>
        <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.85, ease: easeOut }} style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 5vw, 4rem)", fontWeight: 300, lineHeight: 1.1, maxWidth: "22ch", letterSpacing: "-0.01em" }}>"Comprar menos, pero mejor. Vestirse con conciencia."</motion.h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "2rem", paddingTop: "1.5rem", borderTop: "1px solid rgba(255,255,255,0.1)", marginTop: "0.5rem" }}>
        {pillars.map(({ label, desc }, i) => (
            <motion.div key={label} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1, ease: easeOut }}>
            <p style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.88rem", letterSpacing: "0.14em", textTransform: "uppercase", color: t.accent, marginBottom: "0.6rem" }}>{label}</p>
            <p style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.95rem", lineHeight: 1.7, color: "rgba(255,255,255,0.7)" }}>{desc}</p>
            </motion.div>
        ))}
        </div>
        </div>
        </section>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// FOOTER
// ─────────────────────────────────────────────────────────────────────────────
function Footer() {
    const [sostenibilidadOpen, setSostenibilidadOpen] = useState(false)
    const [ubicacionOpen, setUbicacionOpen] = useState(false)
    const cols = [
        { title: "Tienda",       links: ["+150 Diseños de Autor", "SINPE Movil Check", "Mayorista", "Retail", "Balanz"] },
        { title: "Información",  links: ["Envíos y devoluciones", "Sostenibilidad", "Ubicación", "Contacto"] },
    ]

    const handleLinkClick = (link) => {
        switch (link) {
            case "+150 Diseños de Autor": case "Retail": document.getElementById("catalog")?.scrollIntoView({ behavior: "smooth" }); break;
            case "Balanz": window.scrollTo({ top: 0, behavior: "smooth" }); break;
            case "SINPE Movil Check": window.open(generateWhatsAppLink("Hola, quiero ayuda con un SINPE Móvil"), "_blank"); break;
            case "Mayorista": window.open(generateWhatsAppLink("Hola, quiero ser socio mayorista"), "_blank"); break;
            case "Envíos y devoluciones": window.open(generateWhatsAppLink("Hola, tengo una queja sobre envíos o devoluciones"), "_blank"); break;
            case "Sostenibilidad": setSostenibilidadOpen(true); break;
            case "Ubicación": setUbicacionOpen(true); break;
            case "Contacto": window.open(generateWhatsAppLink("Hola, quiero conversar con el equipo"), "_blank"); break;
        }
    }

    const direccion = "Avenida Central entre calle 7 y 9, Comercial Plazavenida, San José, Costa Rica"

    return (
        <footer style={{ backgroundColor: "#141210", padding: "clamp(3.5rem, 7vw, 6rem) clamp(1.5rem, 5vw, 3.5rem) clamp(1.5rem, 3vw, 2.5rem)" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", display: "grid", gridTemplateColumns: "1.4fr repeat(2, 1fr)", gap: "3rem" }}>
        <div>
        <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.3rem", letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 300, color: "#FFFFFF", marginBottom: "1.1rem" }}>Balanz Maison</p>
        <p style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.76rem", color: "rgba(255, 255, 255, 0.6)", lineHeight: 1.75, maxWidth: "22ch" }}>Moda de autores conscientes</p>
        <p style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.52rem", color: "rgba(255, 255, 255, 0.6)", lineHeight: 1.75, maxWidth: "22ch" }}>{direccion}</p>
        </div>
        {cols.map(({ title, links }) => (
            <div key={title}>
            <p style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.6rem", letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(255, 255, 255, 0.45)", marginBottom: "1.25rem" }}>{title}</p>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.65rem" }}>
            {links.map(link => (
                <li key={link}><a href="#" onClick={(e) => { e.preventDefault(); handleLinkClick(link); }} style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.79rem", color: "rgba(255, 255, 255, 0.55)", textDecoration: "none", cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.color = "#FFFFFF"} onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.55)"}>{link}</a></li>
            ))}
            </ul>
            </div>
        ))}
        </div>
        <div style={{ maxWidth: 1400, margin: "2.5rem auto 0", paddingTop: "1.5rem", borderTop: "1px solid rgba(255, 255, 255, 0.1)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
        <a href="https://www.dac-cloud.com" target="_blank" rel="noopener noreferrer" style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.6rem", color: "rgba(255,255,255,0.35)", letterSpacing: "0.08em", textDecoration: "none" }}>© 2026 DAC</a>
        <a href="https://www.dac-cloud.com" target="_blank" rel="noopener noreferrer" style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.6rem", color: "rgba(255,255,255,0.35)", letterSpacing: "0.08em", textDecoration: "none" }}>Datos protegidos | Tecnología Sostenible</a>
        </div>
        {/* Modales Sostenibilidad y Ubicación */}
        <AnimatePresence>
        {sostenibilidadOpen && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSostenibilidadOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 400, backgroundColor: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
            <motion.div variants={modalVariants} initial="hidden" animate="visible" exit="exit" onClick={e => e.stopPropagation()} style={{ backgroundColor: t.glassBg, backdropFilter: "blur(20px)", border: `1px solid ${t.glassBorder}`, borderRadius: 24, maxWidth: "500px", width: "100%", padding: "2rem", position: "relative" }}>
            <button onClick={() => setSostenibilidadOpen(false)} style={{ position: "absolute", top: "1rem", right: "1rem", background: "none", border: "none", color: t.muted }}><X size={20} /></button>
            <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.8rem", fontWeight: 300, marginBottom: "1rem", color: t.text }}>Sostenibilidad</h3>
            <p style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.9rem", lineHeight: 1.6, color: t.muted }}>En Balanze, la moda consciente se apoya en tecnología responsable. Gracias a nuestra infraestructura con <strong>DAC</strong>, operamos bajo estándares de alta eficiencia energética y fomentamos una cadena de suministro local. Al priorizar materiales premium y eliminar los plásticos de un solo uso en nuestros envíos, transformamos cada compra en un paso hacia un futuro ético y sostenible.</p>
            </motion.div>
            </motion.div>
        )}
        </AnimatePresence>
        <AnimatePresence>
        {ubicacionOpen && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setUbicacionOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 400, backgroundColor: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
            <motion.div variants={modalVariants} initial="hidden" animate="visible" exit="exit" onClick={e => e.stopPropagation()} style={{ backgroundColor: t.glassBg, backdropFilter: "blur(20px)", border: `1px solid ${t.glassBorder}`, borderRadius: 24, maxWidth: "400px", width: "100%", padding: "2rem", textAlign: "center" }}>
            <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.6rem", fontWeight: 300, marginBottom: "1rem", color: t.text }}>¿Cómo llegar?</h3>
            <p style={{ fontFamily: "'Jost', sans-serif", fontSize: "0.8rem", marginBottom: "1.5rem", color: t.muted }}>{direccion}</p>
            <div style={{ display: "flex", gap: "1rem", justifyContent: "center" }}>
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(direccion)}`} target="_blank" rel="noopener noreferrer" style={{ padding: "0.6rem 1.2rem", backgroundColor: "#4285F4", color: "white", textDecoration: "none", borderRadius: 40, fontFamily: "'Jost', sans-serif", fontSize: "0.75rem" }}>Google Maps</a>
            <a href={`https://www.waze.com/ul?q=${encodeURIComponent(direccion)}&navigate=yes`} target="_blank" rel="noopener noreferrer" style={{ padding: "0.6rem 1.2rem", backgroundColor: "#33CCFF", color: "white", textDecoration: "none", borderRadius: 40, fontFamily: "'Jost', sans-serif", fontSize: "0.75rem" }}>Waze</a>
            </div>
            </motion.div>
            </motion.div>
        )}
        </AnimatePresence>
        </footer>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────────────────────────────────────
export default function App() {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [cart, setCart] = useState([])
  const [isCartOpen, setIsCartOpen] = useState(false)
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false)
  const cartIconRef = useRef(null)
  const [particles, setParticles] = useState([])

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true)
        setError(null)
        const medusaProducts = await fetchMedusaProducts(50)
        const normalized = medusaProducts.map(normalizeMedusaProduct)
        setProducts(normalized)
      } catch (err) {
        console.error("Error loading products from Medusa:", err)
        setError(err.message || "Error al cargar los productos")
      } finally {
        setLoading(false)
      }
    }
    loadProducts()
  }, [])

  const addToCart = useCallback((product, selectedVariant = null) => {
    // Si no se pasa variante, usar la primera (por compatibilidad)
    const variant = selectedVariant || product.variants?.[0];
    if (!variant) return;
  
    // Construir un título de variante legible (ej: "S / Black")
    const variantTitle = variant.options
      ?.map(o => o.value)
      .filter(Boolean)
      .join(' / ') || variant.title || '';
  
    setCart(prev => {
      // Buscar si ya existe el mismo producto con la misma variante
      const existing = prev.find(item => item.product.id === product.id && item.variant_id === variant.id);
      if (existing) {
        return prev.map(item =>
          item.product.id === product.id && item.variant_id === variant.id
            ? { ...item, qty: item.qty + 1 }
            : item
        );
      }
      return [...prev, {
        product,
        variant_id: variant.id,
        variant_title: variantTitle,
        qty: 1,
        price: variant.price, // Precio de la variante seleccionada
      }];
    });
    setIsCartOpen(true);
  }, []);

  const increment = useCallback((variantId) => {
    setCart(prev => prev.map(item =>
      item.variant_id === variantId ? { ...item, qty: item.qty + 1 } : item
    ))
  }, [])

  const decrement = useCallback((variantId) => {
    setCart(prev => prev.map(item =>
      item.variant_id === variantId ? { ...item, qty: item.qty - 1 } : item
    ).filter(item => item.qty > 0))
  }, [])

  const remove = useCallback((variantId) => {
    setCart(prev => prev.filter(item => item.variant_id !== variantId))
  }, [])

  const openCheckout = () => {
    setIsCartOpen(false)
    setTimeout(() => setIsCheckoutOpen(true), 300)
  }

  const handleFlyStart = (startPos) => {
    if (cartIconRef.current) {
      const rect = cartIconRef.current.getBoundingClientRect()
      const target = { x: rect.left + rect.width/2, y: rect.top + rect.height/2 }
      const id = Date.now()
      setParticles(prev => [...prev, { id, startPos, target }])
      setTimeout(() => setParticles(prev => prev.filter(p => p.id !== id)), 700)
    }
  }

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const cartCount = cart.reduce((s, { qty }) => s + qty, 0)

  return (
    <div style={{ backgroundColor: t.cream, minHeight: "100vh" }}>
      {particles.map(p => (
        <ConfirmationParticle key={p.id} startPos={p.startPos} targetPos={p.target} onComplete={() => setParticles(prev => prev.filter(x => x.id !== p.id))} />
      ))}
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; -webkit-font-smoothing: antialiased; }
        body { background-color: #1A1A1B; overflow-x: hidden; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #333; border-radius: 99px; }
        img { max-width: 100%; }
        @media (max-width: 768px) {
          footer > div:first-child > div { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 500px) {
          footer > div:first-child > div { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <Navbar cartCount={cartCount} onCartOpen={() => setIsCartOpen(true)} cartIconRef={cartIconRef} />
      <main>
        <Hero />
        <VideoReels products={products} />
        <CatalogSection products={products} onAddToCart={addToCart} loading={loading} error={error} onFlyStart={handleFlyStart} />
        <EditorialStrip />
      </main>
      <Footer />

      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} cart={cart} onIncrement={increment} onDecrement={decrement} onRemove={remove} onOpenCheckout={openCheckout} />
      <CheckoutModal isOpen={isCheckoutOpen} onClose={() => setIsCheckoutOpen(false)} cart={cart} onClearCart={clearCart} />
    </div>
  )
}